import {Folio} from "crqlar-react-common/dist/models/billing/folio.model";
import {FolioItem} from "crqlar-react-common/dist/models/billing/folioItem.model";
import {FolioItemService} from "crqlar-react-common/dist/services/billing/folioItem.service";
import {QueryClient} from "react-query";
import {serverTableQueryKey} from "../../../../common/serverTable/useServerTable";
import {DigitalSigningService} from "../../../../digitalSigning/digitalSigning.service";
import {DIGITAL_SIGNING_STALE_TIME, digitalSigningFolioKey} from "../../../../digitalSigning/digitalSigningQueries";

/** The legacy folio list's `useList` id ("Folio" menu entry): its query keys start with it. */
export const LEGACY_FOLIO_LIST_ID = 'FoliosList';

/** The folio dialog's list of a reservation's folios, all of them - the header's folio numbers and the list view's rows. */
export const FOLIO_DIALOG_LIST_KEY = 'folioDialogFolios';

/** The list view's table: the dialog's list searched, sorted and paged, a page at a time. */
export const FOLIO_DIALOG_TABLE_ID = 'folio-dialog-folios';

/** The folio the dialog shows. */
export const FOLIO_DIALOG_FOLIO_KEY = 'folioDialogFolio';

/**
 * A folio's saved lines as the dialog reads them. Under the legacy screens' ['folioItems', id] (whose
 * query functions differ), so the Charge flow's refresh of that key after repricing the reservation's
 * line reaches the dialog too.
 */
export const folioItemsKey = (folioId?: string) => ['folioItems', folioId, 'folioDialog'];

/** Switching back to a folio shows its lines without reading them again for this long. */
export const FOLIO_ITEMS_STALE_TIME = 30 * 1000;

/** How many of the reservation's folios get their lines read ahead - there are rarely more than a few. */
const READ_AHEAD_LIMIT = 10;

export const fetchFolioItems = async (folioId: string): Promise<FolioItem[]> => {
    const items = await FolioItemService.getAllFolioItems({
        folioIds: [folioId],
        limit: {offset: 0, limit: 100, sort: [{field: 'id', direction: 'ASC'}]},
    });
    return items.content || [];
};

/**
 * Reads the folios' lines ahead, so switching shows them at once. Lines read within the stale time
 * are kept, a read already running is joined, and a failed one is left to the folio's own query.
 */
export const prefetchFolioItems = (queryClient: QueryClient, folios: Folio[]) => {
    folios.slice(0, READ_AHEAD_LIMIT).forEach(folio => {
        const folioId = folio.id;
        if (!folioId) return;
        queryClient.prefetchQuery(folioItemsKey(folioId), () => fetchFolioItems(folioId), {staleTime: FOLIO_ITEMS_STALE_TIME});
        // Its signing too: the lines wait for it, and the folio view shows it without a spinner.
        // A reservation's folio or a package's has one
        if (folio.reservationId || folio.reservationGroupId) {
            queryClient.prefetchQuery(
                digitalSigningFolioKey(folioId),
                () => DigitalSigningService.latestForFolio(folioId),
                {staleTime: DIGITAL_SIGNING_STALE_TIME}
            );
        }
    });
};

/**
 * The folio as a folio list the dialog read has it - the same /list endpoint getById reads - so a
 * switch shows its header at once while the folio itself is read again.
 */
export const cachedListFolio = (queryClient: QueryClient, folioId?: string): Folio | undefined => {
    if (!folioId) return undefined;
    for (const [, folios] of queryClient.getQueriesData<Folio[] | undefined>(FOLIO_DIALOG_LIST_KEY)) {
        const folio = folios?.find(candidate => candidate.id === folioId);
        if (folio) return folio;
    }
    return undefined;
};

/** Every folio list reads again: the dialog's own and the legacy one, wherever they are open. */
export const invalidateFolioLists = (queryClient: QueryClient) => {
    queryClient.invalidateQueries(LEGACY_FOLIO_LIST_ID);
    queryClient.invalidateQueries(FOLIO_DIALOG_LIST_KEY);
    // The list view's table is mounted only while shown, and a remount would first show its cached
    // pages as they were: they are made again meanwhile - from the list's new read, so after it -, and
    // coming back shows the folios as they are now
    queryClient.invalidateQueries(serverTableQueryKey(FOLIO_DIALOG_TABLE_ID), {refetchInactive: true});
};
